import {
  HttpClient,
  HttpErrorResponse,
  HttpResponse,
} from '@angular/common/http';
import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BeraterService } from '../../../services/berater.service';
import { Zeugnis } from '../../../shared/model/zeugnis.model';
import { Berater, IBerater } from '../../../shared/model/berater.model';
import { IProjekt } from '../../../shared/model/projekt.model';
import moment from 'moment';
import { environment } from '../../../../environments/environment';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { PossessivPipe } from '../../../shared/possessiv.pipe';
import { ReflexivPipe } from '../../../shared/reflexiv.pipe';
import { GenderedPipe, SalutationPipe } from '../../../shared/gendered.pipe';
import { MatError, MatFormField, MatLabel, MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { MatButton } from '@angular/material/button';
import { MatInput, MatSuffix } from '@angular/material/input';
import {
  MatDatepicker,
  MatDatepickerInput,
  MatDatepickerToggle,
} from '@angular/material/datepicker';
import { DatePipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import {
  CategoryWithGroups,
  getCategoriesWithGroups,
} from './certificate-blocks.constants';

function unique(value, index, self) {
  return self.indexOf(value) === index;
}

function flatten(accumulator, currentValue) {
  return accumulator.concat(currentValue);
}

interface MySelectItem {
  label: string;
  value: string;
}

@Component({
  selector: 'app-report-card',
  templateUrl: './report-card.component.html',
  styleUrls: ['./report-card.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    PossessivPipe,
    ReflexivPipe,
    GenderedPipe,
    MatSelect,
    MatOption,
    MatButton,
    MatLabel,
    MatInput,
    MatSuffix,
    MatFormField,
    MatDatepicker,
    MatDatepickerInput,
    MatDatepickerToggle,
    TranslatePipe,
    SalutationPipe,
    MatError,
    DatePipe,
  ],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
})
export class ReportCardComponent implements OnInit {
  zeugnis: Zeugnis = new Zeugnis();
  berater: Berater;
  language: string = 'de';
  categories: CategoryWithGroups[] = [];
  birthDateMoment: moment.Moment | null = null;
  today = moment();
  endDateMoment: moment.Moment | null = null;
  hireDateMoment: moment.Moment | null = null;
  downloadAttempted = false;

  certificateTypes: MySelectItem[] = [];
  salutations: MySelectItem[] = [
    {
      label: 'certificateCreator.certificateInputs.titleOptions.female',
      value: 'Frau',
    },
    {
      label: 'certificateCreator.certificateInputs.titleOptions.male',
      value: 'Herr',
    },
  ];
  companies: MySelectItem[] = [
    {
      label: 'SPIRIT-TESTING Software & Services GmbH',
      value: 'SPIRIT-TESTING Software & Services GmbH',
    },
    {
      label: 'TestSolutions GmbH',
      value: 'TestSolutions GmbH',
    },
  ];

  constructor(
    protected route: ActivatedRoute,
    protected beraterService: BeraterService,
    private http: HttpClient,
    private genderedPipe: GenderedPipe,
  ) {}

  ngOnInit() {
    this.route.params.subscribe((params) => {
      this.loadConsultant(params['id']);
    });

    this.certificateTypes = [
      {
        label: 'certificateCreator.certificateTypes.workCertificate',
        value: 'Arbeitszeugnis',
      },
      {
        label: 'certificateCreator.certificateTypes.internshipCertificate',
        value: 'Praktikumszeugnis',
      },
      {
        label: 'certificateCreator.certificateTypes.trainingCertificate',
        value: 'Ausbildungszeugnis',
      },
      {
        label: 'certificateCreator.certificateTypes.interimCertificate',
        value: 'Zwischenzeugnis',
      },
    ];
  }

  onBirthDateChange(date: moment.Moment | null): void {
    this.zeugnis.dateOfBirth = date ? date.format('YYYY-MM-DD') : null;
  }

  onEndDateChange(date: moment.Moment | null): void {
    this.zeugnis.enddatum = date ? date.format('DD.MM.YYYY') : '';
  }

  onHireDateChange(date: moment.Moment | null): void {
    this.zeugnis.einstellungsdatum = date ? date.format('DD.MM.YYYY') : '';
  }

  onCertificateTypeChange(): void {
    this.clearSectionFields();
    this.updateCategories();
  }

  changeLanguage(): void {
    this.clearSectionFields();
    this.updateCategories();
  }

  private updateCategories(): void {
    if (!this.zeugnis.artDesZeugnis) {
      this.categories = [];
      return;
    }
    this.categories = getCategoriesWithGroups(
      this.zeugnis.artDesZeugnis,
      this.language,
    );
  }

  private clearSectionFields(): void {
    const fields = [
      'fachwissen', 'weiterbildung', 'auffassungsgabe', 'leistungsbereitschaft',
      'belastbarkeit', 'arbeitsweise', 'zuverlaessigkeit', 'arbeitsergebnis',
      'zusammenfassendeBeurteilung', 'verhalten', 'schlussformulierung',
    ];
    fields.forEach((f) => ((this.zeugnis as any)[f] = ''));
  }

  isWorkCertificate(): boolean {
    return this.zeugnis.artDesZeugnis !== '' && this.zeugnis.artDesZeugnis !== 'Zwischenzeugnis';
  }

  getCertificateField(field: string): string {
    return (this.zeugnis as any)[field] ?? '';
  }

  setCertificateField(field: string, text: string): void {
    (this.zeugnis as any)[field] = text;
  }

  getPreviewText(field: string): string {
    const text: string = (this.zeugnis as any)[field] ?? '';
    return text.replace(/\{DATUM\}/g, this.zeugnis.enddatum || 'TT.MM.JJJJ');
  }

  isDownloadDisabled(): boolean {
    return !this.zeugnis.artDesZeugnis
      || !this.zeugnis.einstellungsdatum
      || !this.zeugnis.vorname
      || !this.zeugnis.nachname
      || !this.zeugnis.rolle
      || !this.zeugnis.letzteProjektrolle
      || (this.isWorkCertificate() && !this.zeugnis.enddatum);
  }

  downloadCertificate() {
    this.downloadAttempted = true;
    this.applyTextTransformations();
    this.http
      .post(
        `${environment.apiConfig.uri}api/zeugnis/${this.language}`,
        this.zeugnis,
        { responseType: 'blob' },
      )
      .subscribe({
        next: (blob: Blob) => {
          const downloadURL = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = downloadURL;
          a.download = `Zeugnis-${this.berater.vorname}-${this.berater.nachname}.docx`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(downloadURL);
        },
        error: (err) => console.error('Fehler beim Download:', err),
      });
  }

  private applyTextTransformations(): void {
    const fields = [
      'fachwissen', 'weiterbildung', 'auffassungsgabe', 'leistungsbereitschaft',
      'belastbarkeit', 'arbeitsweise', 'zuverlaessigkeit', 'arbeitsergebnis',
      'zusammenfassendeBeurteilung', 'verhalten', 'schlussformulierung',
      'beurteilung', 'grundDesZeugnisses', 'verabschiedung',
    ];
    fields.forEach((field) => {
      let text: string = (this.zeugnis as any)[field];
      if (text) {
        text = text.replace(/\{DATUM\}/g, this.zeugnis.enddatum || '');
        (this.zeugnis as any)[field] = this.genderedPipe.transform(
          text,
          this.zeugnis.anrede,
          this.zeugnis.nachname,
          this.language,
        );
      }
    });
  }

  private loadConsultant(login: string): void {
    this.beraterService.find(login).subscribe({
      next: (res: HttpResponse<IBerater>) => {
        this.berater = Berater.fromJson(res.body);
        this.zeugnis.vorname = this.berater.vorname;
        this.zeugnis.nachname = this.berater.nachname;
        const parsed = moment(this.berater.personaldaten.jahrgang, 'DD.MM.YYYY', true);
        if (parsed.isValid()) {
          this.zeugnis.dateOfBirth = parsed.format('YYYY-MM-DD');
          this.birthDateMoment = parsed;
        } else {
          this.zeugnis.dateOfBirth = null;
          this.birthDateMoment = null;
        }

        this.loadSalutation();
        this.loadRole();
        this.loadStartDate();
        this.loadCompany();
        this.loadProjectRole();
        this.loadTasks();
        this.loadTools();
      },
      error: (res: HttpErrorResponse) => {
        console.log(res);
      },
    });
  }

  private loadSalutation() {
    this.zeugnis.anrede =
      this.berater.personaldaten.intro.indexOf('Frau') >= 0 ? 'Frau' : 'Herr';
  }

  private loadRole() {
    this.zeugnis.rolle = this.berater.personaldaten.position;
  }

  private loadStartDate() {
    const eintrittsdatum = this.berater.personaldaten?.hireDate;
    if (eintrittsdatum) {
      const date = moment(eintrittsdatum);
      if (date.isValid()) {
        this.zeugnis.einstellungsdatum = date.format('DD.MM.YYYY');
        this.hireDateMoment = date;
      }
    }
  }

  private loadCompany() {
    const companyName = this.berater.werdegang[0].unternehmen;
    this.zeugnis.firma =
      companyName.toLowerCase().indexOf('solutions') >= 0
        ? 'TestSolutions GmbH'
        : 'SPIRIT-TESTING Software & Services GmbH';
  }

  private loadProjectRole() {
    this.zeugnis.letzteProjektrolle = this.berater.projekte[0].stelle;
  }

  private loadTasks() {
    this.zeugnis.aufgaben = this.findRelevantProjects()
      .map((p) => p.tasks)
      .reduce(flatten, [])
      .map((task) => task.caption)
      .sort()
      .filter(unique);
  }

  private loadTools() {
    this.zeugnis.werkzeuge = this.findRelevantProjects()
      .map((p) => p.tasks)
      .reduce(flatten, [])
      .map((task) => task.skills)
      .reduce(flatten, [])
      .filter(
        (skill) =>
          skill.isProgrammiersprache ||
          skill.isWerkzeug ||
          skill.isTechnologie,
      )
      .map((skill) => skill.faehigkeit)
      .sort()
      .filter(unique);
  }

  private findRelevantProjects(): IProjekt[] {
    return this.berater.projekte.filter((project) =>
      moment(project.beginn).isAfter(moment(this.zeugnis.einstellungsdatum)),
    );
  }
}
